<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        SearchInput,
        PageLoading,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import {
        getFormatDateAndTime,
        replaceURL,
        showError,
        decodeSearchParams,
        encodeSearchParams,
        viewLog,
        getFormatDateAndTimeWithSeconds,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { LogTypes } from "../../Utils/utilEnums.svelte";

    export let params = {};

    let isLoadingExceptionLogs = false,
        returnedExceptionLogsArr = [],
        filters = {};

    let page = 0,
        pageSize = 25,
        count;

    const LIMIT_TEXT_LENGTH = 50;

    const columns = [
        {
            key: "id",
            title: "ID",
            headerClass: "text-left pl-3 cursor-default w-[10%] rounded-tl-xl",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => v.id,
        },
        {
            key: "content",
            title: "Content",
            headerClass: "text-left pl-3 cursor-default w-[70%]",
            class: "text-left px-3 cursor-pointer w-[70%]",
            value: (v) => _getTextWithTrim(v.content),
            csvContent: (v) => _getTextWithTrim(v.content, true),
        },
        {
            key: "createdDate",
            title: "Create Date",
            headerClass: "text-left pl-3 cursor-default w-[20%] rounded-tr-xl",
            class: "text-left px-3 cursor-pointer w-[20%]",
            value: (v) => getFormatDateAndTimeWithSeconds(v.createdDate),
        },
    ];

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        loadExceptionLogs(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];
            }

            updateURL();
        }
    };

    const loadExceptionLogs = async (start, limit, params) => {
        isLoadingExceptionLogs = true;

        await apiMethods.getMethod
            .getExceptionLogs(start, limit, params)
            .then((res) => _handleExceptionLogs(res));

        isLoadingExceptionLogs = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadExceptionLogs(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/ExceptionLogs/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadExceptionLogs(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadExceptionLogs(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        viewLog(LogTypes.Exception, rowData.id);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    // HELPER FUNCTIONS
    const _handleExceptionLogs = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnedExceptionLogsArr = [...res.results];

            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _getTextWithTrim = (text, isCsvContent = false) => {
        if (isCsvContent) return text;

        const displayText =
            text.length <= LIMIT_TEXT_LENGTH
                ? text
                : text.slice(0, LIMIT_TEXT_LENGTH) + "...";

        return `
                <div class="w-full break-all">
                    ${displayText}
                </div>
                `;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-row items-center justify-between flex-wrap">
        <div class="flex gap-4 flex-wrap items-end">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isDisable={isLoadingExceptionLogs}
                isLong
            />
        </div>
    </div>

    <div class="relative flex-1">
        {#if isLoadingExceptionLogs}
            <PageLoading />
        {:else}
            <div class="overflow-y-auto absolute inset-0">
                <SvelteTable
                    {columns}
                    rows={returnedExceptionLogsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnedExceptionLogsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnedExceptionLogsArr,
            fileName: "Exception Logs",
            allDataApi: async () =>
                await apiMethods.getMethod.getExceptionLogs(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
